<template>
  <w-sliding-popup :type="popupType" :ref="popupRef" :popupWidth="popupWidth" title="장애 분석" @close="onClose">
    <div class="fault-analysis-view" slot="content" v-loading="loading" :element-loading-text="loadingText">
      <div class="pdf-wrapper save-for-pdf">
        <el-row class="condition-set-box box mr-b10 event-info">
          <!-- 이벤트 이력 정보 -->
            <ul>
              <li><span class="stress">이벤트 발생 시각</span> {{ eventCheckDatetimeString }}</li>
              <li>
                <span class="stress">자원 정보</span>
                {{eventTargetInfo}}
              </li>
              <li style="width:100%;display:flex;">
                <span style="flex:1;margin-top:3px;">
                  <span class="stress">이벤트 정보</span>
                  <div class="eventlevel-tag" :style="eventlevelStyle">
                    {{getValueAfterCheckEmpty(eventHistData.eventlevelName)}}
                  </div>
                  <span>{{getValueAfterCheckEmpty(eventHistData.eventMessage)}}</span>
                </span>
                <span style="display:inline-block;margin-left:auto;margin-top:auto;" v-show="!isTrue(isExportingPdf)" class="tool">
                  <el-button class="smart-primary" type="secondary" @click="exportToPdfFile">PDF 저장</el-button>
                  <el-button class="smart-primary" type="primary" @click="openEventSmartAnalyzerPopup">Smart Analyzer</el-button>
                </span>
              </li>
            </ul>
        </el-row>
        <el-row>
          <el-collapse class="square-icon mr-b15" v-model="activeCollapseItems">
            <!-- 장애 조치 추천 -->
            <w-collapse-item :name="collapseItemConst.RECOMMEND" title="장애 조치 추천" v-show="visibleRecommendTroubleshooting">
              <w-recommend-handling-trouble-view :target-event-info="eventHistData" ref="recommendHandlingTroubleView" @complete-load="onCompleteLoadRecommendTroubleshooting"/>
            </w-collapse-item>
            <!-- 이벤트 발생 장비 분석 -->
            <w-collapse-item :name="collapseItemConst.TARGET" title="이벤트 발생 장비 분석">
              <template slot="title">
                <span v-if="eventHistData.managetypeId === 'SVC'" class="condition-text mr-l10" style="font-size:14px;">
                  {{seviceEventTargetObjLabel}}
                </span>
                <el-button class="mr-l10" @click="movePerformanceAnalysisView(null)"><i class="fas fa-external-link-alt mr-r2"/>상세 분석</el-button>
              </template>
              <w-fault-analysis-info-view :data="faultAnalysisInfo" :exporting-pdf="isExportingPdf" :exporting-pdf-for-ie="isExportingPdfForIe" />
            </w-collapse-item>
            <!-- [인터페이스] 네트워크 장비 분석 -->
            <w-collapse-item v-if="!isNil(netdeviceFaultAnalysisInfo)" :name="collapseItemConst.NETDEVICE" title="네트워크 장비 분석">
              <template slot="title">
                <el-button class="mr-l10" @click="movePerformanceAnalysisView(eventHistData.parentObjId)"><i class="fas fa-external-link-alt mr-r2"/>상세 분석</el-button>
              </template>
              <w-fault-analysis-info-view :data="netdeviceFaultAnalysisInfo" :exporting-pdf="isExportingPdf" :exporting-pdf-for-ie="isExportingPdfForIe" />
            </w-collapse-item>
            <!-- 연관 이벤트 정보 -->
            <w-collapse-item :name="collapseItemConst.EVENT" title="연관 이벤트 정보">
              <template slot="title">
                <span v-show="!isNil(eventPeriodConditionLabel)" class="condition-text mr-l10">
                  {{eventPeriodConditionLabel}}
                </span>
              </template>
              <w-related-event-info-view style="margin-top: -10px;" :seq-number="eventHistSeqNumber" :exporting-pdf-for-ie="isExportingPdfForIe" />
            </w-collapse-item>
            <!-- 연관 장비 분석 -->
            <w-collapse-item :name="collapseItemConst.ASSOICATION" title="연관 장비 분석" v-if="!isNil(association)">
              <el-table-wrapper class="mr-b25" :data="association" :columns="associationColumns" highlight-current-row row-class-name="c-pointer" :show-search-tool="false" @cell-click="loadAssociationFaultAnalysisInfo" :pagination="false">
              </el-table-wrapper>
              <div v-if="!isNil(selectedAssociation)" class="assoication-info-wrapper">
                <div class="header title-bar vertical-center-box">
                  <span class="title mr-r10">[{{selectedAssociation.objDefineName}}] {{selectedAssociation.objName}}</span>
                  <el-button @click="movePerformanceAnalysisView(selectedAssociation.objId)"><i class="fas fa-external-link-alt mr-r2"/>상세 분석</el-button>
                </div>
                <div class="content">
                  <w-fault-analysis-info-view v-loading="assoicationLoading" v-show="!isNil(associationFaultAnalysisInfo)" :data="associationFaultAnalysisInfo" :exporting-pdf="isExportingPdf" :exporting-pdf-for-ie="isExportingPdfForIe" />
                </div>
              </div>
            </w-collapse-item>
          </el-collapse>
        </el-row>
      </div>
    </div>
  </w-sliding-popup>
</template>

<script>
import { isNil, isEmpty, forEach } from 'lodash';
import JsPdf from 'jspdf';
import canvg from 'canvg-browser';
import Highcharts from 'highcharts';
import Html2Canvas from 'html2canvas';
import moment from 'moment';
import MsgUtil from '@/utils/message';
import eventHistApi from '@/api/event/event-hist';
import { isTrue } from '@/utils/boolean';
import { getDateToString } from '@/utils/date';
import { getValueAfterCheckEmpty, EMPTY_TEXT } from '@/utils/string';
import { routePathByObjEventHist } from '@/utils/router';
import { openEventSmartAnalyzerPopup, closeFaultAnalysisPopup } from '@/utils/event';
import WRelatedEventInfoView from './components/RelatedEventInfoView';
import WFaultAnalysisInfoView from './components/FaultAnalysisInfoView';
import WRecommendHandlingTroubleView from './components/RecommendHandlingTroubleView';
import WCollapseItem from './components/CollapseItem';
import { getEventPeriodCondition, getPeriodConditionLabel } from './utils/fault-analysis';

const collapseItemConst = {
  RECOMMEND: 'recommend', // 장애 조치 추천
  EVENT: 'event', // 연관 이벤트 정보
  TARGET: 'targetAnalysis', // 이벤트 발생 장비 분석,
  ASSOICATION: 'assoicationAnalysis', // 연관 장비 분석
  NETDEVICE: 'netdevice', // 네트워크 장비 분석 정보
};
export default {
  name: 'WFaultAnalysisPopup',
  components: {
    WRelatedEventInfoView,
    WFaultAnalysisInfoView,
    WRecommendHandlingTroubleView,
    WCollapseItem,
  },
  data() {
    return {
      collapseItemConst,
      popupType: 'full',
      popupWidth: null,
      popupRef: 'faultAnalysisPopup',
      /* 이벤트 이력 정보 */
      eventHistData: {},
      eventPeriodConditionLabel: null,
      /* 장애 분석 정보 */
      faultAnalysisInfo: null, // { obj, eventHistData, faultAnalysisInfo, association }
      seviceEventTargetObjLabel: null,
      /* 연관 장비 정보 */
      association: null,
      associationColumns: [
        {
          prop: 'objDefineName',
          label: '구분',
        },
        {
          prop: 'objName',
          label: '장비명',
        },
        {
          prop: 'ipaddress',
          label: 'IP주소',
          formatter: this.formatterForIp,
        },
      ],
      associationFaultAnalysisInfo: null,
      assoicationLoading: false,
      selectedAssociation: null,
      /* 네트워크 장비 분석 정보 (인터페이스) */
      netdeviceFaultAnalysisInfo: null,
      /* PDF */
      isExportingPdf: false,
      isExportingPdfForIe: false,
      /* 기타 */
      visibleRecommendTroubleshooting: false,
      activeCollapseItems: [collapseItemConst.RECOMMEND, collapseItemConst.EVENT, collapseItemConst.TARGET],
      loadingText: null,
      loading: false,
    };
  },
  computed: {
    visible() {
      return this.$store.state.event.showFaultAnalysisPopup;
    },
    showSettingView() {
      return this.$store.state.common.showSettingView;
    },
    eventHistSeqNumber() {
      return this.$store.state.event.faultAnalysisSeqNumber;
    },
    eventCheckDatetimeString() {
      return isEmpty(this.eventHistData) || isNil(this.eventHistData.checkDatetime) ? EMPTY_TEXT : this.getDateToString(this.eventHistData.checkDatetime);
    },
    eventTargetInfo() {
      if (isEmpty(this.eventHistData)) {
        return EMPTY_TEXT;
      }
      const data = this.eventHistData;
      let objName;
      let ipaddress;
      if (data.managetypeId === 'E2E') {
        objName = getValueAfterCheckEmpty(data.agentObjName);
        ipaddress = isNil(data.agentIpaddress) ? '' : `(${data.agentIpaddress})`;
      } else {
        objName = getValueAfterCheckEmpty(data.objName);
        ipaddress = isNil(data.ipaddress) ? '' : `(${data.ipaddress})`;
      }
      return `${objName}${ipaddress} > ${getValueAfterCheckEmpty(data.rsctypeName)} > ${getValueAfterCheckEmpty(data.rscName)} > ${getValueAfterCheckEmpty(data.rscKeyName)}`;
    },
    eventlevelStyle() {
      let color;
      let backgroundColor;
      if (isEmpty(this.eventHistData)) {
        color = '#000';
        backgroundColor = '#fff';
      } else {
        color = this.eventHistData.eventlevelColorText;
        backgroundColor = this.eventHistData.eventlevelColorBg;
      }
      return {
        color,
        backgroundColor,
      };
    },
  },
  watch: {
    visible: {
      handler(value) {
        if (value) {
          this.open();
        } else {
          this.close();
        }
      },
    },
    eventHistSeqNumber: {
      handler(value) {
        this.reset();
        if (!isNil(value)) {
          this.loadData();
        }
      },
    },
    eventHistData: {
      handler(value) {
        if (isEmpty(value)) {
          this.eventPeriodConditionLabel = null;
        } else {
          const period = getEventPeriodCondition(value.checkDatetime);
          this.eventPeriodConditionLabel = `(${getPeriodConditionLabel(period.startDatetime, period.endDatetime)})`;
        }
      },
    },
    faultAnalysisInfo: {
      handler(value) {
        if (!isEmpty(value)) {
          this.association = isEmpty(value.association) ? null : value.association;
          if (!isEmpty(this.faultAnalysisInfo.obj)) {
            const obj = this.faultAnalysisInfo.obj;
            this.seviceEventTargetObjLabel = `[${this.getValueAfterCheckEmpty(obj.objDefineName)}] ${this.getValueAfterCheckEmpty(obj.objName)}`;
          } else {
            this.seviceEventTargetObjLabel = null;
          }
        } else {
          this.association = null;
          this.seviceEventTargetObjLabel = null;
        }
        this.associationFaultAnalysisInfo = null;
        this.selectedAssociation = null;
      },
    },
  },
  beforeDestroy() {
    if (this.visible) {
      this.close();
    }
  },
  methods: {
    isNil,
    isTrue,
    getDateToString,
    getValueAfterCheckEmpty,
    /**
     * 팝업을 연다.
     * @author Ju
     */
    open() {
      if (this.showSettingView) {
        this.popupType = 'setting';
        this.popupWidth = '100%';
      } else {
        this.popupType = 'full';
        this.popupWidth = null;
      }
      this.$refs[this.popupRef].open();
    },
    /**
     * 팝업을 닫는다.
     * @author Ju
     */
    close() {
      this.$refs[this.popupRef].close();
    },
    /**
     * 팝업 close 이벤트 핸들러
     * @author Ju
     */
    onClose() {
      closeFaultAnalysisPopup();
    },
    /**
     * 화면을 초기화한다.
     * @author Ju
     */
    reset() {
      this.eventHistData = {};
      this.faultAnalysisInfo = null;
      this.netdeviceFaultAnalysisInfo = null;
      this.visibleRecommendTroubleshooting = false;

      this.loading = false;
      this.loadingText = null;
      this.isExportingPdf = false;
      this.isExportingPdfForIe = false;
    },
    /**
     * 장애 분석 정보를 조회한다.
     * @author Ju
     */
    loadData() {
      this.loadEventHist();
      this.loadFaultAnalysisInfo();
    },
    /**
     * 이벤트 이력 정보를 조회한다.
     * @author Ju
     */
    loadEventHist() {
      eventHistApi
        .getEventHistBySeqNumber(this.eventHistSeqNumber)
        .then((response) => {
          if (response.data.success === true) {
            this.eventHistData = response.data.data;
            if (this.eventHistData.objDefineId === 'NETINTERFACE' && !isNil(this.eventHistData.parentObjId)) {
              this.loadNetdeviceFaultAnalysisInfo();
            }
          } else {
            this.$message(MsgUtil.getResultMsgData(MsgUtil.action.VIEW, '이벤트 이력 정보', false));
          }
        })
        .catch((e) => {
          this.$message(MsgUtil.getResultMsgData(MsgUtil.action.VIEW, '이벤트 이력 정보', false));
          throw new Error(e);
        });
    },
    /**
     * 이벤트 장애 분석 정보를 조회한다.
     * @author Ju
     */
    loadFaultAnalysisInfo() {
      this.loading = true;
      eventHistApi
        .getFaultAnalysisInfoOfEventHist(this.eventHistSeqNumber)
        .then((response) => {
          if (response.data.success === true) {
            this.faultAnalysisInfo = response.data.data;
            this.$nextTick(() => {
              this.activateAllCollapseItems();
            });
          } else {
            this.$message(MsgUtil.getResultMsgData(MsgUtil.action.VIEW, '장애 분석 정보', false));
          }
        })
        .catch((e) => {
          this.$message(MsgUtil.getResultMsgData(MsgUtil.action.VIEW, '장애 분석 정보', false));
          throw new Error(e);
        }).finally(() => {
          this.loading = false;
        });
    },
    /**
     * 연관 장비 장애 분석 정보를 조회한다.
     * @author Ju
     * @param  {[type]}  row [description]
     */
    loadAssociationFaultAnalysisInfo(row) {
      this.assoicationLoading = true;
      eventHistApi
        .getFaultAnalysisInfoOfEventHist(this.faultAnalysisInfo.eventHistData.seqNumber, row.objId)
        .then((response) => {
          if (response.data.success === true) {
            this.associationFaultAnalysisInfo = { selectedObjId: row.objId, ...response.data.data };
            this.selectedAssociation = row;
          } else {
            this.$message(MsgUtil.getResultMsgData(MsgUtil.action.VIEW, '연관 장비 장애 분석 정보', false));
          }
        })
        .catch((e) => {
          this.$message(MsgUtil.getResultMsgData(MsgUtil.action.VIEW, '연관 장비 장애 분석 정보', false));
          throw new Error(e);
        }).finally(() => {
          this.assoicationLoading = false;
        });
    },
    /**
     * 이벤트 발생 대상이 인터페이스일 때 인터페이스의 부모 장비(네트워크 장비)의 분석 정보를 제공한다.
     * @author Ju
     * @return {Boolean} [description]
     */
    loadNetdeviceFaultAnalysisInfo() {
      eventHistApi
        .getFaultAnalysisInfoOfEventHist(this.eventHistData.seqNumber, this.eventHistData.parentObjId)
        .then((response) => {
          if (response.data.success === true) {
            this.netdeviceFaultAnalysisInfo = response.data.data;
            this.activeCollapseItems.push(collapseItemConst.NETDEVICE);
          } else {
            this.$message(MsgUtil.getResultMsgData(MsgUtil.action.VIEW, '네트워크 장비 장애 분석 정보', false));
          }
        })
        .catch((e) => {
          this.$message(MsgUtil.getResultMsgData(MsgUtil.action.VIEW, '네트워크 장비 장애 분석 정보', false));
          throw new Error(e);
        });
    },
    /**
     * 장애 조치 추천 로드 완료 이벤트 핸들러
     * @author Ju
     * @param  {[type]} data 응답 데이터
     * @return {[type]}      [description]
     */
    onCompleteLoadRecommendTroubleshooting(data) {
      this.visibleRecommendTroubleshooting = !isEmpty(data) && (!isEmpty(data.kedb) || !isEmpty(data.troubleshooting));
    },
    /**
     * 모든 collapse item을 활성화시킨다.
     * @author Ju
     */
    activateAllCollapseItems() {
      const items = [collapseItemConst.RECOMMEND, collapseItemConst.EVENT, collapseItemConst.TARGET];
      if (!isEmpty(this.association)) {
        items.push(collapseItemConst.ASSOICATION);
      }
      if (!isEmpty(this.netdeviceFaultAnalysisInfo)) {
        items.push(collapseItemConst.NETDEVICE);
      }
      this.activeCollapseItems = items;
    },
    /**
     * IP 주소 formatter
     * @author Ju
     * @param  {[type]} row [description]
     * @return {[type]}     [description]
     */
    formatterForIp(row) {
      return this.getValueAfterCheckEmpty(row.ipaddress);
    },
    /**
     * 장비의 성능/운영 > 분석 화면으로 이동한다.
     * @author Ju
     * @param  {Number}  objId 대상 장비. 없을 경우 이벤트가 발생한 장비가 대상이 된다.
     */
    movePerformanceAnalysisView(objId) {
      const eventHistData = this.eventHistData;
      // SKT 요청 개발. 시스로그 관련 이벤트에서 rscKey가 _이 아닐때 성능/운영 > 분석 > 시스템로그 테이블에 정책 필터링 기능
      if (eventHistData.rsctypeId === 'SYSLOG' && eventHistData.rscId === 'UNCHECKED_SYSLOG') {
        debugger;
        const params = {
          syslogRuleId: eventHistData.rscKey,
        };
        routePathByObjEventHist(this, isNil(objId) ? this.eventHistData.objId : objId, null, this.eventHistData.checkDatetime, null, true, params);
      } else {
        routePathByObjEventHist(this, isNil(objId) ? this.eventHistData.objId : objId, null, this.eventHistData.checkDatetime, null, true);
      }
    },
    /**
     * 이벤트 이력에 대한 Smart Analyzer 팝업을 연다.
     * @author Ju
     */
    openEventSmartAnalyzerPopup() {
      openEventSmartAnalyzerPopup([this.eventHistData.seqNumber]);
    },
    /**
     * 장애 분석 정보 PDF 출력
     * @author jeee12
     * @return {[type]} [description]
     */
    exportToPdfFile() {
      this.loadingText = 'PDF로 저장 중..';
      this.loading = true;
      // (브라우저 공통)
      // 이상 징후 아이콘이 PDF 출력 시 제대로 표현되지 않기 때문에 다른 아이콘으로 치환해준다.
      // 이벤트 정보 영역 버튼 안보이게 처리
      this.isExportingPdf = true;

      // 이전에 dom update 시점 문제 해결을 위해 el의 querySelector로 돔 요소에 접근하여 속성 변경하도록 구현
      // => IE의 strict 모드에서는 readable 속성을 수정할 수 없기 때문에 pdf 출력되지 않는 현상 발생
      // dom update 완료된 시점에 화면 캡처 수행되도록 nextTick 훅 추가
      this.$nextTick(() => {
        this.exportPdf().then(() => {
          this.$message(MsgUtil.getResultMsgData('생성', 'PDF', true));
        }).catch((e) => {
          this.$message(MsgUtil.getResultMsgData('생성', 'PDF', false));
          throw new Error(e);
        }).finally(() => {
          // 변경한 이상 징후 아이콘 클래스를 되돌린다.
          this.isExportingPdf = false;
          // (IE) svg 영역 표현되지 않는 문제 해결
          this.isExportingPdfForIe = false;
          this.loading = false;
        });
      });
    },
    exportPdf() {
      const agent = navigator.userAgent.toLowerCase();
      const isIE = (navigator.appName === 'Netscape' && agent.indexOf('trident') !== -1) || (agent.indexOf('mise') !== -1);

      return new Promise((resolve, reject) => {
        try {
          /* IE 이슈 대응 - 하이차트 html2canvas로 캡처되지 않는 문제 */
          if (isIE) {
            // 브라우저가 IE일 경우 하이차트 영역을 이미지로 변환 후 출력한다.
            this.isExportingPdfForIe = true;
            // 하이차트 목록을 조회해온 뒤 > export-pdf 클래스의 차트만 가공한다.
            forEach(Highcharts.charts, (chart) => {
              if (!isEmpty(chart) && chart.renderTo.className.indexOf('export-pdf') !== -1) {
                const className = chart.renderTo.className;
                const chartWidth = chart.chartWidth;
                const chartHeight = chart.chartHeight;

                // 하이차트 svg 영역을 이미지로 변경하기 위한 canvas 엘리먼트의 임시 생성
                const canvas = document.createElement('canvas');
                canvas.setAttribute('width', chartWidth);
                canvas.setAttribute('height', chartHeight);
                if (className.indexOf('circular') === -1) {
                  // 파이차트가 아닐 때는 타이틀 제거
                  canvg(canvas, chart.getSVG({ chart: { width: chartWidth, height: chartHeight }, title: false }));
                } else {
                  // 파이차트일 때는 타이틀 유지
                  canvg(canvas, chart.getSVG({ chart: { width: chartWidth, height: chartHeight } }));
                }

                const imgUrl = canvas.toDataURL('image/png');
                const imgEl = this.$el.querySelector(`img.${className}`);
                imgEl.setAttribute('width', chartWidth);
                imgEl.setAttribute('height', chartHeight);
                imgEl.setAttribute('src', imgUrl);
              }
            });

            /* 이미지 렌더링 속도 맞추기 위해 500ms 이후 출력 함수 실행 */
            setTimeout(() => {
              this.getHtmlToPdf(this.eventHistData, resolve);
            }, 500);
          } else {
            // 브라우저가 IE가 아닐 때에는 가공 없이 출력한다.
            this.getHtmlToPdf(this.eventHistData, resolve);
          }
        } catch (e) {
          reject(e);
        }
      });
    },
    getHtmlToPdf(eventHistData, resolve) {
      // source : PDF로 변환할 HTML 영역
      const source = this.$el.querySelector('.save-for-pdf');

      // 성능 개선 : 출력 외 영역(팝업 외 영역)은 클로닝/렌더링하지 않도록 attribute 추가
      const app = document.querySelector('#app');
      app.setAttribute('data-html2canvas-ignore', true);

      // 단위 변환(px > pt) 시 필요한 constants
      const webDPI = 96; // Window 기준 WEB DPI
      const pdfDPI = 72; // PDF 캔버스 기준 DPI
      const A4Width = 210; // A4 가로길이 (mm)
      const A4Height = 297; // A4 세로길이 (mm)
      const convertInch = 2.54; // 인치 변환 계수

      // A4용지 사이즈를 WEB DPI 기준 픽셀 사이즈로 변환
      const A4PxHeight = (A4Height / (10 * convertInch)) * webDPI;

      // 화질 개선 Scale Factor (커질수록 화질 향상, 기본값은 1), 출력 크기 조절용 Parameter (출력 영역 가로길이 > PDF 가로길이)
      const scaleFactor = 2;
      const scaleParam = 1.2;

      // PDF 상하/좌우 여백
      const margin = 50 * scaleFactor;

      /**
       * jdpdf 객체 생성
       * @param [용지방향] 'p'(default) : 세로, 'l' : 가로
       * @param [단위] 'pt'(default), 'mm' ...
       * @param [PDF 사이즈] 'a4'(default), 'letter', 'a6' ... [width, height]로 용지 크기를 정해줄 수 있음
       */
      const pdf = new JsPdf('p', 'pt', 'a4');

      // PDF의 가로 길이(좌우여백 제외, a4기준)
      // const pageWidth = 190;
      // const pageHeight = pageWidth * 1.414;
      // const imgWidth = pageWidth;

      Html2Canvas(source, { scale: scaleFactor, allowTaint: true, logging: true }).then((canvas) => {
        // 캡쳐된 이미지의 세로 길이 단위 변환 (px > mm) 후 출력해야 할 PDF의 페이지 수 계산
        // const imgHeight = (canvas.height * imgWidth) / canvas.width;
        // const pageNum = imgHeight / pageHeight;
        // page개수 계산시 실제 pdf영역 길이(A4)를 기준으로 나눈다
        const pdfHeight = (A4PxHeight * scaleFactor * scaleParam) - margin;
        const pageNum = canvas.height / pdfHeight;

        // page개수 만큼 반복해 이미지를 PDF 객체에 set
        for (let page = 0; page < pageNum; page += 1) {
          const sourceImage = canvas;

          // canvas 엘리먼트에 캡쳐된 이미지를 한 페이지 사이즈로 잘라 저장하기 위한 parameter
          // 1. Source Image
          const sourceX = 0;
          const sourceY = pageNum > 1 ? ((A4PxHeight * scaleFactor * scaleParam) - margin) * page : 0;
          const sourceWidth = sourceImage.width;
          const sourceHeight = pageNum > 1 ? (A4PxHeight * scaleFactor * scaleParam) - margin : sourceImage.height;

          // 2. Canvas
          const destX = margin; // LEFT margin
          const destY = margin; // TOP margin
          const destWidth = sourceImage.width - (margin * 2); // 좌우 여백 통일
          const destHeight = sourceHeight; // 상하 여백 통일

          // canvas 엘리먼트의 width와 height 지정 (WEB 픽셀 사이즈 기준 > Windows일 때)
          const onePageCanvas = document.createElement('canvas');

          onePageCanvas.setAttribute('width', sourceImage.width);
          onePageCanvas.setAttribute('height', pageNum > 1 ? sourceImage.height - margin : sourceImage.height);

          const ctx = onePageCanvas.getContext('2d');
          // canvas 엘리먼트에 캡쳐된 이미지를 현재 페이지에 맞게 잘라 set
          ctx.drawImage(sourceImage, sourceX, sourceY, sourceWidth, sourceHeight, destX, destY, destWidth, destHeight);

          // png > jpeg로 형식 변경하면서 캔버스의 투명 영역이 검정색으로 바뀌는 문제를 수정하기 위한 이미지 비트 연산작업
          const tempImg = ctx.getImageData(0, 0, onePageCanvas.width, onePageCanvas.height);
          const data = tempImg.data;
          for (let bit = 0; bit < data.length; bit += 4) {
            if (data[bit + 3] < 255) {
              data[bit] = 255 - data[bit];
              data[bit + 1] = 255 - data[bit + 1];
              data[bit + 2] = 255 - data[bit + 2];
              data[bit + 3] = 255 - data[bit + 3];
            }
          }
          ctx.putImageData(tempImg, 0, 0);

          const imgData = onePageCanvas.toDataURL('image/jpeg', 1.0);
          const width = onePageCanvas.width;
          const height = onePageCanvas.height;

          if (page > 0) {
            // a4사이즈로 페이지를 추가함 (PDF 캔버스 DPI 기준)
            pdf.addPage((A4Width / (10 * convertInch)) * pdfDPI, (A4Height / (10 * convertInch)) * pdfDPI);
          }
          pdf.setPage(page + 1);

          // 최대 너비(pdf 용지 너비)보다 좁으면 이미지 너비로 출력 영역 설정
          const onePageImageWidth = (width * 0.6) / scaleFactor > pdf.internal.pageSize.getWidth() ? pdf.internal.pageSize.getWidth() : (width * 0.6) / scaleFactor;
          pdf.addImage(imgData, 'JPEG', 0, 0, onePageImageWidth, (height * 0.6) / scaleFactor, '', 'FAST');
        }
        pdf.save(`${`장애분석정보_${eventHistData.objName}_${eventHistData.rsctypeName}_${eventHistData.rscName}_${moment(new Date()).format('YYYYMMDD')}`}.pdf`);
      }).catch((e) => {
        throw new Error(e);
      }).finally(() => {
        // 임시로 추가했던 data-htrml2canvas-ignore attribute 제거
        app.removeAttribute('data-html2canvas-ignore');
        resolve();
      });
    },
  },
};
</script>

<style lang="scss">
.fault-analysis-view {
  .event-info li {
    line-height: 20px;
    .tool {
      position: relative;
      bottom: 6px;
    }
  }

  .content-title {
    display: inline-block;
    font-weight: 500;
    font-size: 14px;
  }

  .condition-text {
    display: inline-block;
    color: #999;
    font-size: 12px;
  }

  .sub-title {
    color: #797b80;
    font-weight: 500;
  }

  .abnormal-message {
    color: #e43757;
    margin-left: 2px;
    &:before {
      content: "\E7A3";
      font-family: element-icons!important;
      font-weight: 400;
      font-variant: normal;
      line-height: 1;
    }
  }

  .box-table {
    border-leftm: 1px solid #f1f2f5;
    border-right: 1px solid #f1f2f5;
    border-bottom: 1px solid #f1f2f5;
  }

  .pdf-wrapper {
    width: 100%;
  }

  .tab-button .el-tabs__content {
    min-height: 100px;
  }

  .assoication-info-wrapper {
    border: 1px solid #ddd;
    & > .header {
      padding: 5px 10px;
      background: #f1f2f5;
      & > .title {
        font-size: 14px;
        font-weight: 500;
      }
    }

    & > .content {
      padding: 20px;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/popup/FaultAnalysisPopup/index.vue